import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsRequisition, useBudgetLignes, useRequisition, useStructures, type RequisitionSaisie } from '@/api/hooks';
import type { Requisition } from '@/api/types';
import { voitToutLeFonds } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { Chargement, PageHeader } from '@/components/common';
import { ActiviteSousActivite, CircuitPrevu, SituationLigneChoisie, useDeclareMemorial } from '@/components/documents';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { montant } from '@/lib/utils';

interface Valeurs {
  objet: string;
  structureCode: string;
  ligneBudgetaireId: string;
  justification: string;
  articles: Array<{ designation: string; quantite: number; unite: string; estimationUsd: number }>;
  delaiSouhaite: string;
  urgence: boolean;
  justificationUrgence: string;
  memorial: boolean;
}

const articleVide = () => ({ designation: '', quantite: 1, unite: 'pièce', estimationUsd: 0 });

const depuisRequisition = (r: Requisition): Valeurs => ({
  objet: r.objet,
  structureCode: r.structure?.code ?? '',
  ligneBudgetaireId: r.ligneBudgetaireId,
  justification: r.justification,
  articles: r.articles.length ? r.articles : [articleVide()],
  delaiSouhaite: r.delaiSouhaite ?? '',
  urgence: r.urgence,
  justificationUrgence: r.justificationUrgence ?? '',
  memorial: r.memorial,
});

const versSaisie = (v: Valeurs): RequisitionSaisie => ({
  objet: v.objet.trim(),
  structureCode: v.structureCode || undefined,
  ligneBudgetaireId: v.ligneBudgetaireId,
  justification: v.justification.trim(),
  articles: v.articles.filter((a) => a.designation.trim()).map((a) => ({ designation: a.designation.trim(), quantite: Number(a.quantite) || 0, unite: a.unite.trim() || 'pièce', estimationUsd: Number(a.estimationUsd) || 0 })),
  delaiSouhaite: v.delaiSouhaite.trim() || undefined,
  urgence: v.urgence,
  justificationUrgence: v.urgence ? v.justificationUrgence.trim() || undefined : undefined,
  memorial: v.memorial,
});

/** Réquisition (v1.1, § 10) : nouvelle ou en correction, émise par les services. */
export function RequisitionFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const u = session!.utilisateur;
  const existant = useRequisition(id);
  const structures = useStructures();
  const actions = useActionsRequisition();
  const declareMemorial = useDeclareMemorial(u.structureCode);
  const form = useForm<Valeurs>({
    defaultValues: { objet: '', structureCode: u.structureCode, ligneBudgetaireId: '', justification: '', articles: [articleVide()], delaiSouhaite: '', urgence: false, justificationUrgence: '', memorial: false },
  });
  const articles = useFieldArray({ control: form.control, name: 'articles' });
  const valeurs = useWatch({ control: form.control });
  const [toutesLignes, setToutesLignes] = useState(false);
  const budget = useBudgetLignes(toutesLignes ? undefined : valeurs.structureCode || u.structureCode);

  useEffect(() => {
    if (existant.data) form.reset(depuisRequisition(existant.data.requisition));
  }, [existant.data, form]);

  // La liste des structures arrive après le rendu : la valeur par défaut est réappliquée au champ natif.
  useEffect(() => {
    if (structures.data?.length && !id) form.setValue('structureCode', form.getValues('structureCode') || u.structureCode);
  }, [structures.data, id, form, u.structureCode]);

  if (id && existant.isLoading) return <Chargement />;
  const total = (valeurs.articles ?? []).reduce((s, a) => s + (Number(a?.quantite) || 0) * (Number(a?.estimationUsd) || 0), 0);
  const ligne = budget.data?.find((l) => l.id === valeurs.ligneBudgetaireId);
  const structure = structures.data?.find((s) => s.code === (valeurs.structureCode || u.structureCode));
  const initial = existant.data && existant.data.requisition.statut !== 'BROUILLON' ? existant.data.requisition.montantEstime : 0;

  const enregistrer = form.handleSubmit(
    async (v) => {
      const corps = versSaisie(v);
      if (!corps.articles.length) return toast.error('Ajoutez au moins un article ou une prestation.');
      try {
        const r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
        toast.success(id ? 'Réquisition modifiée.' : 'Réquisition enregistrée en brouillon.');
        navigate(`/requisitions/${r.requisition.id}`);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
      }
    },
    () => toast.error("Renseignez l'objet, la sous-activité et les articles demandés."),
  );

  return (
    <form onSubmit={enregistrer} className="space-y-6">
      <PageHeader
        retour={
          <Link to={id ? `/requisitions/${id}` : '/requisitions'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Corriger la réquisition ${existant.data?.requisition.numero ?? ''}` : 'Nouvelle réquisition'}
        sousTitre={`Émise par ${u.prenom} ${u.nom}. Décrivez le besoin et les articles ou prestations demandés avec leur estimation ; les achats consulteront les fournisseurs après autorisation.`}
        actions={
          <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
            {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Besoin</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="objet">Objet</Label>
            <Input id="objet" {...form.register('objet', { required: true, minLength: 5 })} placeholder="Équipement informatique de la cellule d'écoute" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="structure">Structure demandeuse</Label>
            <SelectNatif id="structure" {...form.register('structureCode')}>
              {(structures.data ?? []).map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delai">Délai souhaité</Label>
            <Input id="delai" {...form.register('delaiSouhaite')} placeholder="Avant le 15 novembre 2026" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <ActiviteSousActivite lignes={budget.data ?? []} ligneId={valeurs.ligneBudgetaireId ?? ''} setLigneId={(l) => form.setValue('ligneBudgetaireId', l, { shouldDirty: true, shouldValidate: true })} obligatoire />
            {voitToutLeFonds(u.habilitations) && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox checked={toutesLignes} onCheckedChange={(c) => setToutesLignes(c === true)} />
                Afficher toutes les lignes du Fonds
              </label>
            )}
            <SituationLigneChoisie ligne={ligne} montantDocument={total} montantInitial={initial} />
          </div>
          <div className="flex flex-col gap-2 md:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <Controller control={form.control} name="urgence" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
              Demande urgente
            </label>
            {declareMemorial && (
              <label className="flex items-center gap-2 text-sm">
                <Controller control={form.control} name="memorial" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
                Activité du Mémorial (visa du Coordonnateur avant le Directeur)
              </label>
            )}
          </div>
          {valeurs.urgence && (
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="justif-urgence">Justification de l'urgence (obligatoire)</Label>
              <Textarea id="justif-urgence" rows={2} {...form.register('justificationUrgence')} />
            </div>
          )}
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="justification">Justification du besoin (20 caractères au moins)</Label>
            <Textarea id="justification" rows={3} {...form.register('justification')} placeholder="À quoi serviront les articles demandés, pour quelle activité, pour qui." />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Articles ou prestations demandés <span className="num float-right">{montant(total, true)}</span>
          </CardTitle>
          <CardDescription>Désignation, quantité, unité et estimation unitaire en USD. Le montant estimé réserve le budget jusqu'au bon de commande.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {articles.fields.map((f, i) => (
            <div key={f.id} className="grid gap-2 md:grid-cols-[1fr_6rem_7rem_9rem_auto]">
              <Input placeholder="Désignation" {...form.register(`articles.${i}.designation`)} />
              <Input className="text-right" type="number" min={0} step="0.01" placeholder="Qté" {...form.register(`articles.${i}.quantite`, { valueAsNumber: true })} />
              <Input placeholder="Unité" {...form.register(`articles.${i}.unite`)} />
              <Input className="text-right" type="number" min={0} step="0.01" placeholder="Estimation unitaire" {...form.register(`articles.${i}.estimationUsd`, { valueAsNumber: true })} />
              <Button type="button" variant="ghost" size="icon" onClick={() => articles.remove(i)} aria-label="Retirer" disabled={articles.fields.length === 1}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => articles.append(articleVide())}>
            <Plus className="size-4" /> Ajouter un article
          </Button>
          <div className="pt-2">
            <CircuitPrevu type="REQUISITION" montant={total} operationnel={structure?.operationnelle ?? false} structure={valeurs.structureCode || u.structureCode} memorial={Boolean(valeurs.memorial)} />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={id ? `/requisitions/${id}` : '/requisitions'}>Abandonner</Link>
        </Button>
        <Button type="submit" disabled={actions.creer.isPending || actions.modifier.isPending}>
          {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
        </Button>
      </div>
    </form>
  );
}
